import React, { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAnimal, useEvents, updateHealthFlagLocal, updateEliteFlagLocal, updateGoodMotherFlagLocal, useCalvesOf, useFarms, useLots, deleteAnimalLocal, createEventLocal } from "@/lib/repo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Edit2, Plus, Sparkles, Trash2, Beef, Stethoscope, Star, Heart } from "lucide-react";
import { TimelineEvent } from "@/components/TimelineEvent";
import { AnimalChipsCard } from "@/components/AnimalChipsCard";
import { formatDate, ageLabel, CATEGORY_LABEL } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
const SEX_LABEL = { female: "Hembra", male: "Macho" };
const STATUS_VARIANT = {
  active: "present", sold: "muted", slaughtered: "missing", dead: "missing",
  lost: "ochre", in_transit: "ochre", transferred: "ochre",
};
const STATUS_LABEL = {
  active: "Activo", sold: "Vendido", slaughtered: "Sacrificado", dead: "Muerto",
  lost: "Extraviado", in_transit: "En tránsito", transferred: "Trasladado",
};
const EVENT_TYPES = [
  { value: "vaccination", label: "Vacunación" },
  { value: "palpation",   label: "Palpación" },
  { value: "transfer",    label: "Traslado" },
  { value: "observation", label: "Observación" },
  { value: "treatment",   label: "Tratamiento / patología" },
  { value: "death",       label: "Muerte" },
  { value: "sale",        label: "Venta" },
];

export default function AnimalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const animal = useAnimal(id);
  const events = useEvents(id);
  // Fix Q (2026-10-21): finca y lote en Datos; partos (crías) dentro del Historial.
  const farms = useFarms();
  const lots = useLots(animal?.farm_id);
  const calves = useCalvesOf(animal);
  const timeline = useMemo(() => {
    const births = calves.map((c) => ({
      id: `calving-${c.id}`, type: "calving",
      // La fecha de nacimiento viene sin hora: se fija al mediodía para que no se
      // muestre como el día anterior por la zona horaria.
      date: c.birth_date ? `${c.birth_date.slice(0, 10)}T12:00:00` : c.created_at,
      payload: { calf_id: c.id, calf_lomo: c.lomo_number, calf_chip: c.chip_code, calf_sex: c.sex, registered_at: c.created_at },
    }));
    return [...events, ...births].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [events, calves]);
  const farmName = farms.find((f) => f.id === animal?.farm_id)?.name;
  const lotName = lots.find((l) => l.id === animal?.lot_id)?.name;
  const [savingMother, setSavingMother] = useState(false);
  const loading = animal === undefined;
  const [eventForm, setEventForm] = useState({ type: "observation", payload: {}, diagnosis: "pregnant", months: "2", notes: "", pathology: "", medication: "", dose: "" });
  const [eventOpen, setEventOpen] = useState(false);
  const [savingHealth, setSavingHealth] = useState(false);
  const [savingElite, setSavingElite] = useState(false);

  const submitEvent = async () => {
    try {
      let payload = eventForm.payload;
      if (eventForm.type === "palpation") {
        payload = { diagnosis: eventForm.diagnosis, vet_notes: eventForm.notes, registered_by_name: user?.name, registered_by_role: user?.role };
        if (eventForm.diagnosis === "pregnant" && eventForm.months !== "") {
          const months = Number(eventForm.months);
          payload.months = months;
          // Misma fórmula que el backend (Acciones P11): gestación de referencia
          // ~283 días menos lo ya transcurrido según los meses cargados.
          const remainingDays = Math.max(0, 283 - months * 30.4);
          payload.due_date = new Date(Date.now() + remainingDays * 86400000).toISOString();
        }
      }
      if (eventForm.type === "observation") payload = { note: eventForm.notes };
      if (eventForm.type === "vaccination") payload = { vaccine_name: eventForm.notes };
      if (eventForm.type === "treatment")   payload = { pathology: eventForm.pathology, medication: eventForm.medication, dose: eventForm.dose, notes: eventForm.notes };
      await createEventLocal(id, { type: eventForm.type, payload });
      toast.success("Evento registrado");
      setEventOpen(false);
      setEventForm({ type: "observation", payload: {}, diagnosis: "pregnant", months: "2", notes: "", pathology: "", medication: "", dose: "" });
    } catch (e) {
      toast.error(e?.userMessage || "No pude guardar");
    }
  };

  const toggleSick = async () => {
    setSavingHealth(true);
    try {
      // Fix A (2026-08-24): al MARCAR el problema se pide una descripción breve
      // de qué tiene el animal (opcional: si se cancela o se deja vacía, se
      // marca igual). Al desmarcar no se pregunta nada.
      let healthNote = null;
      let treatment = null;
      if (!animal.is_sick) {
        healthNote = window.prompt("¿Qué problema tiene el animal? (ej. piometra, metritis)", animal.health_note || "");
        if (healthNote === null) return;   // canceló: no se cambia nada
        treatment = window.prompt("¿Qué tratamiento se usó? (opcional)", "");
      }
      await updateHealthFlagLocal(id, !animal.is_sick, healthNote);
      // Fix Q (2026-10-21): antes la marca solo vivía en el animal y al marcarlo sano se
      // borraba la nota: no quedaba rastro de piometras/metritis (clave para transplante
      // de embriones). Ahora cada problema y cada alta quedan como evento en el Historial.
      if (!animal.is_sick) {
        await createEventLocal(id, { type: "treatment", payload: { pathology: healthNote || "Sin detalle", medication: treatment || null, source: "health_flag" } });
      } else {
        await createEventLocal(id, { type: "health_ok", payload: { note: animal.health_note ? `Se recuperó de: ${animal.health_note}` : "Marcado como sano", source: "health_flag" } });
      }
      toast.success(!animal.is_sick ? "Marcado como con problema de salud" : "Marcado como sano");
    } catch (e) {
      toast.error(e?.userMessage || "No pude actualizar");
    } finally { setSavingHealth(false); }
  };

  // Fix P (2026-10-06): marca élite (solo el dueño).
  const toggleElite = async () => {
    setSavingElite(true);
    try {
      await updateEliteFlagLocal(id, !animal.is_elite);
      toast.success(!animal.is_elite ? "Marcado como élite" : "Quitado de élite");
    } catch (e) {
      toast.error(e?.userMessage || "No pude actualizar");
    } finally { setSavingElite(false); }
  };

  // Fix Q (2026-10-21): distintivo "Buena madre" (solo el dueño, solo hembras).
  const toggleGoodMother = async () => {
    setSavingMother(true);
    try {
      await updateGoodMotherFlagLocal(id, !animal.is_good_mother);
      toast.success(!animal.is_good_mother ? "Marcada como buena madre" : "Quitada de buenas madres");
    } catch (e) {
      toast.error(e?.userMessage || "No pude actualizar");
    } finally { setSavingMother(false); }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este animal?")) return;
    try { await deleteAnimalLocal(id); toast.success("Animal eliminado"); navigate("/animales"); }
    catch (e) { toast.error(e?.userMessage || "No pude eliminar"); }
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-40 w-full rounded-[var(--radius)]" /><Skeleton className="h-40 w-full rounded-[var(--radius)]" /></div>;
  if (!animal) return null;

  const canEdit = user?.role === "owner";
  const isVet = user?.role === "vet";

  return (
    <div data-testid="animal-detail-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={()=>navigate(-1)} className="h-9 w-9"><ArrowLeft size={18} /></Button>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Ficha</div>
          <h1 className="font-editorial text-[28px] sm:text-[36px] leading-[1.05] tracking-tight">Lomo {animal.lomo_number || "—"}</h1>
        </div>
        {canEdit && (
          <Button asChild variant="outline" size="sm" data-testid="animal-detail-edit-button">
            <Link to={`/animales/${animal.id}/editar`}><Edit2 size={13}/>Editar</Link>
          </Button>
        )}
        {isVet ? (
          <Button asChild size="sm" data-testid="animal-detail-palpation-button">
            <Link to="/acciones/11"><Stethoscope size={13}/>Registrar palpación</Link>
          </Button>
        ) : (
          <Button asChild size="sm" data-testid="animal-detail-ai-button">
            <Link to={`/animales/${animal.id}/ia`}><Sparkles size={13}/>Reporte IA</Link>
          </Button>
        )}
      </div>

      {/* Identity card */}
      <Card>
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-[calc(var(--radius)-0.25rem)] bg-accent text-accent-foreground grid place-items-center shrink-0">
            <Beef size={26} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-mono text-[13px] tracking-[0.06em] tabular-nums text-foreground" data-testid="animal-detail-chip">{animal.chip_code}</div>
              {Array.isArray(animal.chips) && animal.chips.length > 1 && (
                <Badge variant="outline" className="text-[10px]" data-testid="animal-detail-chip-count">
                  +{animal.chips.length - 1} chip{animal.chips.length - 1 === 1 ? "" : "s"} vinculado{animal.chips.length - 1 === 1 ? "" : "s"}
                </Badge>
              )}
            </div>
            <div className="text-[13px] text-muted-foreground mt-1">
              {CATEGORY_LABEL[animal.category] || "Sin categoría"} · {SEX_LABEL[animal.sex]} · {animal.breed} · {ageLabel(animal.birth_date)}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Badge variant={STATUS_VARIANT[animal.status] || "muted"}>{STATUS_LABEL[animal.status] || animal.status}</Badge>
              {animal.is_sick && <Badge variant="ochre" data-testid="animal-detail-sick-badge">Problema de salud</Badge>}
              {animal.is_elite && <Badge variant="warm" data-testid="animal-detail-elite-badge">Élite</Badge>}
              {animal.is_good_mother && <Badge variant="present" data-testid="animal-detail-good-mother-badge">Buena madre</Badge>}
              {/* Fix A (2026-08-24): la descripción del problema, visible junto al badge. */}
              {animal.is_sick && animal.health_note && (
                <span className="text-[12.5px] text-muted-foreground" data-testid="animal-detail-health-note">
                  {animal.health_note}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Button
        variant={animal.is_sick ? "outline" : "ghost"}
        size="sm"
        onClick={toggleSick}
        disabled={savingHealth}
        data-testid="animal-detail-toggle-sick-button"
        className={animal.is_sick ? "border-amber-400 text-amber-700 dark:text-amber-400" : "text-muted-foreground"}
      >
        {animal.is_sick ? "Marcar como sano" : "Marcar problema de salud"}
      </Button>
      {canEdit && (
        <Button
          variant={animal.is_elite ? "outline" : "ghost"}
          size="sm"
          onClick={toggleElite}
          disabled={savingElite}
          data-testid="animal-detail-toggle-elite-button"
          className={animal.is_elite ? "ml-2 border-[hsl(var(--brand-warm))] text-[hsl(var(--brand-warm))]" : "ml-2 text-muted-foreground"}
        >
          <Star size={13} className="mr-1" />
          {animal.is_elite ? "Quitar de élite" : "Marcar como élite"}
        </Button>
      )}
      {canEdit && animal.sex === "female" && (
        <Button
          variant={animal.is_good_mother ? "outline" : "ghost"}
          size="sm"
          onClick={toggleGoodMother}
          disabled={savingMother}
          data-testid="animal-detail-toggle-good-mother-button"
          className={animal.is_good_mother ? "ml-2 border-[hsl(var(--state-present))] text-[hsl(var(--state-present))]" : "ml-2 text-muted-foreground"}
        >
          <Heart size={13} className="mr-1" />
          {animal.is_good_mother ? "Quitar buena madre" : "Buena habilidad materna"}
        </Button>
      )}

      {/* Tabs: Datos / Historial */}
      <Tabs defaultValue="data">
        <TabsList>
          <TabsTrigger value="data" data-testid="animal-tab-data">Datos</TabsTrigger>
          <TabsTrigger value="history" data-testid="animal-tab-history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="mt-4">
          <Card>
            <CardContent className="p-5 sm:p-6 grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-5 text-sm">
              <Field label="Chip RFID" value={<span className="font-mono tabular-nums tracking-[0.04em]">{animal.chip_code}</span>} />
              <Field label="Lomo" value={animal.lomo_number || "-"} />
              <Field label="Tatuaje" value={animal.ear_tattoo || "-"} />
              {animal.ear_tattoo_right && <Field label="Tatuaje oreja der." value={<span className="font-mono">{animal.ear_tattoo_right}</span>} />}
              {animal.ear_tattoo_left && <Field label="Tatuaje oreja izq." value={<span className="font-mono">{animal.ear_tattoo_left}</span>} />}
              {(animal.weight_kg !== null && animal.weight_kg !== undefined) && <Field label="Peso" value={`${animal.weight_kg} kg`} />}
              <Field label="Fecha nacimiento" value={formatDate(animal.birth_date)} mono />
              <Field label="Edad" value={ageLabel(animal.birth_date)} />
              <Field label="Sexo" value={SEX_LABEL[animal.sex]} />
              <Field label="Raza" value={animal.breed || "-"} />
              <Field label="Categoría" value={CATEGORY_LABEL[animal.category] || "-"} />
              {animal.iron_brand && <Field label="Hierro (pierna/paleta)" value={animal.iron_brand} />}
              {(animal.mother_lomo || animal.mother_chip) && (
                <Field label="Madre" value={
                  animal.mother_id
                    ? <Link to={`/animales/${animal.mother_id}`} className="text-primary hover:underline">Lomo {animal.mother_lomo || "—"}</Link>
                    : <span>Lomo {animal.mother_lomo || (animal.mother_chip ? `chip ${animal.mother_chip}` : "—")}</span>
                } />
              )}
              <Field label="Estado" value={<span className="capitalize">{STATUS_LABEL[animal.status] || animal.status}</span>} />
              <Field label="Finca" value={farmName || "-"} />
              <Field label="Lote" value={lotName || "Sin lote"} />
            </CardContent>
          </Card>
          {animal.observations && (
            <Card className="mt-3">
              <CardHeader>
                <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Observaciones</div>
                <CardTitle className="text-[15px] mt-0.5">Notas registradas</CardTitle>
              </CardHeader>
              <CardContent className="text-[14px] text-foreground/85 whitespace-pre-line leading-relaxed">
                {animal.observations}
              </CardContent>
            </Card>
          )}

          {animal.iron_brand_drawing && (
            <Card className="mt-3">
              <CardHeader>
                <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Hierro</div>
                <CardTitle className="text-[15px] mt-0.5">Diseño dibujado</CardTitle>
              </CardHeader>
              <CardContent>
                <img src={animal.iron_brand_drawing} alt="Hierro" className="h-24 w-36 rounded-md border border-border object-contain bg-white" data-testid="animal-detail-iron-drawing" />
              </CardContent>
            </Card>
          )}

          {/* Fix C (2026-08-24): foto real del hierro, además del dibujo (pedido del cliente). */}
          {animal.iron_brand_photo && (
            <Card className="mt-3">
              <CardHeader>
                <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Hierro</div>
                <CardTitle className="text-[15px] mt-0.5">Foto del hierro</CardTitle>
              </CardHeader>
              <CardContent>
                <Dialog>
                  <DialogTrigger asChild>
                    <img
                      src={animal.iron_brand_photo}
                      alt="Foto del hierro"
                      className="max-h-[200px] rounded-md border border-border object-contain bg-white cursor-pointer"
                      data-testid="animal-detail-iron-photo"
                    />
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Foto del hierro</DialogTitle></DialogHeader>
                    <img src={animal.iron_brand_photo} alt="Foto del hierro en tamaño completo" className="w-full rounded-md" />
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          )}

          <div className="mt-3">
            <AnimalChipsCard
              animal={animal}
              canEdit={canEdit}
            />
          </div>

          {canEdit && (
            <div className="flex justify-end mt-4">
              <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={remove} data-testid="animal-delete-button">
                <Trash2 size={14}/>Eliminar animal
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Línea de tiempo</div>
                <CardTitle className="text-[17px] mt-0.5">Historia del animal</CardTitle>
              </div>
              {!isVet && user?.role !== "handler" && (
                <Sheet open={eventOpen} onOpenChange={setEventOpen}>
                  <SheetTrigger asChild>
                    <Button size="sm" data-testid="animal-detail-add-event-button"><Plus size={14}/>Evento</Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="rounded-t-[var(--radius)]">
                    <SheetHeader><SheetTitle className="font-heading">Registrar evento</SheetTitle></SheetHeader>
                    <div className="py-3 space-y-3">
                      <div>
                        <Label className="text-[12px] text-muted-foreground">Tipo</Label>
                        <Select value={eventForm.type} onValueChange={(v)=>setEventForm({ ...eventForm, type: v })}>
                          <SelectTrigger className="h-11" data-testid="event-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                          {EVENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {eventForm.type === "palpation" && (
                      <>
                        <div>
                          <Label className="text-[12px] text-muted-foreground">Diagnóstico</Label>
                          <Select value={eventForm.diagnosis} onValueChange={(v)=>setEventForm({ ...eventForm, diagnosis: v })}>
                            <SelectTrigger className="h-11" data-testid="event-palpation-diagnosis"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pregnant">Preñada</SelectItem>
                              <SelectItem value="empty">Vacía</SelectItem>
                              <SelectItem value="empty_cull">Vacía descarte</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {eventForm.diagnosis === "pregnant" && (
                          <div>
                            <Label className="text-[12px] text-muted-foreground">Meses de preñez</Label>
                            <Input data-testid="event-palpation-months" type="number" inputMode="decimal" placeholder="Ej. 2.5 = 2 meses y 15 días"
                              value={eventForm.months} onChange={(e)=>setEventForm({ ...eventForm, months: e.target.value })} className="h-11" />
                          </div>
                        )}
                      </>
                    )}
                    {eventForm.type === "treatment" && (
                      <>
                        <div>
                          <Label className="text-[12px] text-muted-foreground">Patología / diagnóstico</Label>
                          <Input data-testid="event-pathology" value={eventForm.pathology} onChange={(e)=>setEventForm({ ...eventForm, pathology: e.target.value })} className="h-11" placeholder="Ej. mastitis, cojera…" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[12px] text-muted-foreground">Medicamento</Label>
                            <Input data-testid="event-medication" value={eventForm.medication} onChange={(e)=>setEventForm({ ...eventForm, medication: e.target.value })} className="h-11" />
                          </div>
                          <div>
                            <Label className="text-[12px] text-muted-foreground">Dosis</Label>
                            <Input data-testid="event-dose" value={eventForm.dose} onChange={(e)=>setEventForm({ ...eventForm, dose: e.target.value })} className="h-11" />
                          </div>
                        </div>
                      </>
                    )}
                    <div>
                      <Label className="text-[12px] text-muted-foreground">{eventForm.type === "vaccination" ? "Vacuna" : "Notas"}</Label>
                      <Textarea data-testid="event-notes" value={eventForm.notes} onChange={(e)=>setEventForm({ ...eventForm, notes: e.target.value })} rows={3} />
                    </div>
                    <Button onClick={submitEvent} size="lg" className="w-full" data-testid="event-submit-button">Guardar evento</Button>
                  </div>
                </SheetContent>
              </Sheet>
              )}
            </CardHeader>
            <CardContent>
              {timeline.length === 0 ? (
                <div className="text-center py-8">
                  <p className="font-editorial text-[20px] tracking-tight">Sin eventos registrados</p>
                  <p className="text-[13px] text-muted-foreground mt-1">Sumá vacunación, peso o palpación para construir el historial.</p>
                </div>
              ) : (
                <div data-testid="animal-history-timeline" className="pl-1">
                  {timeline.map((e) => <TimelineEvent key={e.id} event={e} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
      <div className={`text-foreground mt-1 text-[14px] ${mono ? "font-mono tabular-nums" : ""}`}>{value}</div>
    </div>
  );
}
